import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { Box } from '@material-ui/core';

import { useGetDaasProjects } from '../../hooks';
import { ApplicationSetAppNameTemplateSection } from './sections/ApplicationSetAppNameTemplateSection';
import { ApplicationSetBasicInfoSection } from './sections/ApplicationSetBasicInfoSection';
import { ApplicationSetClusterSection } from './sections/ApplicationSetClusterSection';
import { ApplicationSetEditBasicInfoSection } from './sections/ApplicationSetEditBasicInfoSection';
import { ApplicationSetGeneratorsSection } from './sections/ApplicationSetGeneratorsSection';
import { ApplicationSetSourceSection } from './sections/ApplicationSetSourceSection';
import { ApplicationSetSyncOptionsSection } from './sections/ApplicationSetSyncOptionsSection';
import { ApplicationSetTemplatePatchSection } from './sections/ApplicationSetTemplatePatchSection';
import { AutoNamespaceSection } from './sections/AutoNamespaceSection';
import { AutoSyncSection } from './sections/AutoSyncSection';
import { ImageUpdaterSection } from './sections/ImageUpdaterSection';
import { MetadataSection } from './sections/MetadataSection';
import { OwnerSection } from './sections/OwnerSection';
import { ProjectSection } from './sections/ProjectSection';
import { useStyles } from './styles';
import {
  APPLICATION_SET_DEFAULT_DATA,
  ApplicationSetBasicInfoValue,
  AutoCreateNamespaceConfig,
  AutoSyncConfig,
  Cluster,
  ClusterConfiguration,
  DaasApplicationSetInputData,
  DEFAULT_GENERATORS_DATA,
  DEFAULT_IMAGE_UPDATER_CONFIG,
  DEFAULT_TEMPLATE_PATCH_DATA,
  ImageUpdaterConfig,
  KubernetesMetadata,
  SourceConfiguration,
  SyncOption,
} from './types';
import { serializeProjectEntity } from './utils';

export interface DaasApplicationSetInputUiOptions {
  fillEditFormData?: boolean;
}

export const DaasApplicationSetInput = (
  props: FieldExtensionComponentProps<
    DaasApplicationSetInputData,
    DaasApplicationSetInputUiOptions
  >,
) => {
  const { onChange, formData, uiSchema, rawErrors } = props;
  const classes = useStyles();
  const fillEditFormData = uiSchema?.['ui:options']?.fillEditFormData === true;

  // Stabilize rawErrors — Backstage creates a new array reference on every render
  // even when contents are unchanged (typically []). Without this, React.memo on
  // sections is defeated because every section receives a new errors prop each render.
  const stableErrorsRef = useRef<string[]>(rawErrors ?? []);
  const nextErrors = rawErrors ?? [];
  if (
    nextErrors.length !== stableErrorsRef.current.length ||
    nextErrors.some((e, i) => e !== stableErrorsRef.current[i])
  ) {
    stableErrorsRef.current = nextErrors;
  }
  const errors = stableErrorsRef.current;

  const data = formData ?? APPLICATION_SET_DEFAULT_DATA;
  const syncPolicy = useMemo(
    () => data.syncPolicy ?? APPLICATION_SET_DEFAULT_DATA.syncPolicy,
    [data.syncPolicy],
  );

  // Refs to latest values — allow stable callbacks without stale closures
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const syncPolicyRef = useRef(syncPolicy);
  syncPolicyRef.current = syncPolicy;

  const { value: entities, loading } = useGetDaasProjects(false, true);
  const userProjects = useMemo(
    () => entities?.map(entity => serializeProjectEntity(entity)) || [],
    [entities],
  );
  const selectedProject = useMemo(
    () => userProjects.find(project => project.name === data.project),
    [userProjects, data.project],
  );
  const projectClusters: Cluster[] = selectedProject?.clusters ?? [];

  // Stable callbacks. sections memoized with React.memo will not re-render when
  // unrelated fields change
  const handleProjectChange = useCallback((project: string) => {
    onChangeRef.current({
      ...dataRef.current,
      project,
      cluster: null,
      source: null,
    });
  }, []);

  const handleBasicInfoChange = useCallback(
    (v: ApplicationSetBasicInfoValue) => {
      onChangeRef.current({ ...dataRef.current, ...v });
    },
    [],
  );

  const handleEditBasicInfoChange = useCallback(
    (
      v: Pick<
        DaasApplicationSetInputData,
        'applicationSetName' | 'description' | 'applicationSetRef'
      >,
    ) => {
      onChangeRef.current({ ...dataRef.current, ...v });
    },
    [],
  );

  const handleApplicationSetEntitySelect = useCallback(
    (fullData: Partial<DaasApplicationSetInputData>) => {
      const sanitizedData = Object.fromEntries(
        Object.entries(fullData).filter(([, value]) => value !== undefined),
      ) as Partial<DaasApplicationSetInputData>;
      onChangeRef.current({
        ...dataRef.current,
        ...sanitizedData,
        _initialized: true,
      });
    },
    [],
  );

  const handleOwnerChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, owner: v });
  }, []);

  const handleGeneratorsChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, generators: v });
  }, []);

  const handleAppNameTemplateChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, appNameTemplate: v });
  }, []);

  const handleClusterChange = useCallback((v: ClusterConfiguration | null) => {
    onChangeRef.current({ ...dataRef.current, cluster: v });
  }, []);

  const handleSourceChange = useCallback((v: SourceConfiguration | null) => {
    onChangeRef.current({ ...dataRef.current, source: v });
  }, []);

  const handleAutoSyncChange = useCallback((v: AutoSyncConfig) => {
    onChangeRef.current({
      ...dataRef.current,
      syncPolicy: { ...syncPolicyRef.current, autoSync: v },
    });
  }, []);

  const handleAutoNamespaceChange = useCallback(
    (v: AutoCreateNamespaceConfig) => {
      onChangeRef.current({
        ...dataRef.current,
        syncPolicy: { ...syncPolicyRef.current, autoCreateNamespace: v },
      });
    },
    [],
  );

  const handleSyncOptionsChange = useCallback((v: SyncOption[]) => {
    onChangeRef.current({
      ...dataRef.current,
      syncPolicy: { ...syncPolicyRef.current, syncOptions: v },
    });
  }, []);

  const handleMetadataChange = useCallback((v: KubernetesMetadata) => {
    onChangeRef.current({ ...dataRef.current, applicationMetadata: v });
  }, []);

  const handleTemplatePatchChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, templatePatch: v });
  }, []);

  const handleImageUpdaterChange = useCallback((v: ImageUpdaterConfig) => {
    onChangeRef.current({ ...dataRef.current, imageUpdater: v });
  }, []);

  // Initialize defaults once on mount — avoids race condition where simultaneous
  // child effects with the same stale dataRef.current overwrite each other.
  // syncPolicy must be initialized here so downstream catalog actions always.
  useEffect(() => {
    const d = dataRef.current;
    if (
      !d.generators ||
      !d.templatePatch ||
      !d.syncPolicy ||
      !d.applicationMetadata ||
      !d.imageUpdater
    ) {
      onChangeRef.current({
        ...d,
        generators: d.generators || DEFAULT_GENERATORS_DATA,
        templatePatch: d.templatePatch || DEFAULT_TEMPLATE_PATCH_DATA,
        syncPolicy: d.syncPolicy || APPLICATION_SET_DEFAULT_DATA.syncPolicy,
        applicationMetadata:
          d.applicationMetadata ||
          APPLICATION_SET_DEFAULT_DATA.applicationMetadata,
        imageUpdater: d.imageUpdater || DEFAULT_IMAGE_UPDATER_CONFIG,
      });
    }
  }, []);

  // Memoized value slices  only re-render when their own data changes
  const basicInfoValue = useMemo(
    () => ({
      applicationSetName: data.applicationSetName ?? '',
      description: data.description ?? '',
    }),
    [data.applicationSetName, data.description],
  );

  const editBasicInfoValue = useMemo(
    () => ({
      applicationSetName: data.applicationSetName ?? '',
      description: data.description ?? '',
      applicationSetRef: data.applicationSetRef,
    }),
    [data.applicationSetName, data.description, data.applicationSetRef],
  );
  return (
    <Box className={classes.root}>
      <Box className={classes.mainContainer}>
        <ProjectSection
          value={data.project}
          projects={userProjects}
          loading={loading}
          onChange={handleProjectChange}
          errors={errors}
          disabled={fillEditFormData}
        />
        {fillEditFormData ? (
          <ApplicationSetEditBasicInfoSection
            value={editBasicInfoValue}
            onChange={handleEditBasicInfoChange}
            onApplicationSetEntitySelect={handleApplicationSetEntitySelect}
            isFormAlreadyPopulated={!!data._initialized}
            errors={errors}
          />
        ) : (
          <ApplicationSetBasicInfoSection
            value={basicInfoValue}
            selectedProject={data.project}
            onChange={handleBasicInfoChange}
            errors={errors}
          />
        )}
        {fillEditFormData && (
          <OwnerSection
            value={data.owner ?? ''}
            onChange={handleOwnerChange}
            errors={errors}
          />
        )}
        <ApplicationSetGeneratorsSection
          value={data.generators ?? ''}
          onChange={handleGeneratorsChange}
          errors={errors}
        />
        <ApplicationSetAppNameTemplateSection
          value={data.appNameTemplate ?? ''}
          onChange={handleAppNameTemplateChange}
          errors={errors}
        />
        <ApplicationSetClusterSection
          value={data.cluster}
          availableClusters={projectClusters}
          onChange={handleClusterChange}
          errors={errors}
          enableAppSetSupport
        />
        <ApplicationSetSourceSection
          value={data.source}
          onChange={handleSourceChange}
          errors={errors}
        />
        <AutoSyncSection
          value={syncPolicy.autoSync}
          onChange={handleAutoSyncChange}
        />
        <AutoNamespaceSection
          value={syncPolicy.autoCreateNamespace}
          onChange={handleAutoNamespaceChange}
          errors={errors}
          enableAppSetSupport
        />
        <ApplicationSetSyncOptionsSection
          value={syncPolicy.syncOptions}
          onChange={handleSyncOptionsChange}
          errors={errors}
          enableAppSetSupport
        />
        <MetadataSection
          value={data.applicationMetadata}
          onChange={handleMetadataChange}
          errors={errors}
          enableAppSetSupport
        />
        <ImageUpdaterSection
          value={data.imageUpdater ?? DEFAULT_IMAGE_UPDATER_CONFIG}
          onChange={handleImageUpdaterChange}
          source={data.source}
          errors={errors}
        />
        <ApplicationSetTemplatePatchSection
          value={data.templatePatch ?? ''}
          onChange={handleTemplatePatchChange}
          errors={errors}
        />
      </Box>
    </Box>
  );
};
